import { useEffect, useRef } from 'react';

/**
 * Escuta scanners HID (USB) que emulam teclado — incluindo leitores 2D
 * (QR Code, DataMatrix, PDF417). O leitor envia caracteres rapidamente
 * (gap < 50ms entre teclas) e termina com Enter (ou CR/LF para 2D).
 *
 * - Suporta payloads multilinha de QR (newlines internos viram '\n' no buffer).
 * - Ignora eventos vindos de inputs editáveis por padrão (use `captureInInputs`
 *   nas telas operacionais Thick-Finger onde o foco fica em um <input> central).
 * - `prefix` / `suffix` permitem strip de marcadores configuráveis no leitor
 *   (ex.: AIM ID, FNC1 do GS1 DataMatrix).
 */
export interface BarcodeScannerOptions {
  onScan: (code: string) => void;
  minLength?: number;
  intervalMs?: number;
  captureInInputs?: boolean;
  enabled?: boolean;
  prefix?: string;
  suffix?: string;
}

export function useBarcodeScanner({
  onScan,
  minLength = 4,
  intervalMs = 60,
  captureInInputs = false,
  enabled = true,
  prefix,
  suffix,
}: BarcodeScannerOptions) {
  const buffer = useRef('');
  const lastAt = useRef(0);

  useEffect(() => {
    if (!enabled) return;

    function isTypingTarget(target: EventTarget | null): boolean {
      if (!(target instanceof HTMLElement)) return false;
      const tag = target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
      if (target.isContentEditable) return true;
      return false;
    }

    function emit() {
      let code = buffer.current;
      buffer.current = '';
      if (prefix && code.startsWith(prefix)) code = code.slice(prefix.length);
      if (suffix && code.endsWith(suffix)) code = code.slice(0, -suffix.length);
      code = code.trim();
      if (code.length >= minLength) onScan(code);
    }

    function onKey(e: KeyboardEvent) {
      if (e.ctrlKey || e.altKey || e.metaKey) return;
      if (!captureInInputs && isTypingTarget(e.target)) return;

      const now = Date.now();
      // Reset se passou muito tempo (digitação humana entre rajadas).
      if (now - lastAt.current > intervalMs && buffer.current.length === 0) {
        // ok, está iniciando agora
      } else if (now - lastAt.current > intervalMs * 8) {
        buffer.current = '';
      }
      lastAt.current = now;

      if (e.key === 'Enter') {
        if (buffer.current.length >= minLength) {
          e.preventDefault();
          e.stopPropagation();
        }
        emit();
        return;
      }
      if (e.key === 'Tab') {
        // Alguns leitores 2D mandam Tab; tratamos como terminador também.
        if (buffer.current.length >= minLength) {
          e.preventDefault();
          e.stopPropagation();
          emit();
        }
        return;
      }
      if (e.key.length === 1) {
        buffer.current += e.key;
      }
    }

    window.addEventListener('keydown', onKey, { capture: true });
    return () => window.removeEventListener('keydown', onKey, { capture: true } as EventListenerOptions);
  }, [enabled, captureInInputs, intervalMs, minLength, onScan, prefix, suffix]);
}

/**
 * Componente "headless" que monta o listener globalmente. Útil em telas operacionais.
 */
export function GlobalBarcodeListener(props: BarcodeScannerOptions) {
  useBarcodeScanner(props);
  return null;
}
